import React, { createContext, useContext, useState, useCallback, useEffect } from "react";
import { isEqual, isFunction } from "lodash";
import { initialFilter } from "./SuppliersUIHelpers";

const SuppliersUIContext = createContext();

// The selection is mirrored into storage so it survives a refresh or a trip to another screen —
// the same pattern used for products (see ProductsUIContext.js). Only `id`/`supplier_name` are
// stored, since those are the only fields the "Selected Suppliers: N" label and the export
// dialog's preview read.
const SUPPLIER_SELECTION_STORAGE_KEY = "supplier_selected_rows";

const toStoredSupplier = ({ id, supplier_name }) => ({ id, supplier_name });

const readStoredSelection = () => {
  try {
    const stored = JSON.parse(localStorage.getItem(SUPPLIER_SELECTION_STORAGE_KEY)) || {};
    return { ids: Array.isArray(stored.ids) ? stored.ids : [], entities: Array.isArray(stored.entities) ? stored.entities : [] };
  } catch (error) {
    return { ids: [], entities: [] };
  }
};

export function useSuppliersUIContext() {
  return useContext(SuppliersUIContext);
}

export const SuppliersUIConsumer = SuppliersUIContext.Consumer;

export function SuppliersUIProvider({ suppliersUIEvents, children }) {
  const [restoredSelection] = useState(readStoredSelection);
  const [ids, setIds] = useState(restoredSelection.ids);
  const [supplierEntities, setSupplierEntities] = useState(restoredSelection.entities);
  const [customCity, setCustomCity] = useState("");
  const [refreshCount, setRefreshCount] = useState(0);
  const [queryParams, setQueryParamsBase] = useState(initialFilter);
  const [requiredLength, setRequiredLength] = useState(17);
  const setQueryParams = useCallback((nextQueryParams) => {
    setQueryParamsBase((prevQueryParams) => {
      if (isFunction(nextQueryParams)) {
        nextQueryParams = nextQueryParams(prevQueryParams);
      }

      if (isEqual(prevQueryParams, nextQueryParams)) {
        return prevQueryParams;
      }

      return nextQueryParams;
    });
  }, []);

  useEffect(() => {
    try {
      if (ids.length || supplierEntities.length) {
        localStorage.setItem(SUPPLIER_SELECTION_STORAGE_KEY, JSON.stringify({ ids, entities: supplierEntities.map(toStoredSupplier) }));
      } else {
        localStorage.removeItem(SUPPLIER_SELECTION_STORAGE_KEY);
      }
    } catch (error) {
      // Storage full or unavailable: the selection still works for this visit, it just isn't restored.
      console.warn("Could not persist the supplier selection", error);
    }
  }, [ids, supplierEntities]);

  const value = {
    queryParams,
    setIds,
    ids,
    supplierEntities,
    setSupplierEntities,
    setQueryParamsBase,
    setQueryParams,
    newSupplierButtonClick: suppliersUIEvents.newSupplierButtonClick,
    openEditSupplierPage: suppliersUIEvents.openEditSupplierPage,
    openSupplierExportDialog: suppliersUIEvents.openSupplierExportDialog,
    openSupplierImportDialog: suppliersUIEvents.openSupplierImportDialog,
    openDetailSupplierDialog: suppliersUIEvents.openDetailSupplierDialog,
    openSupplierPaymentDialog: suppliersUIEvents.openSupplierPaymentDialog,
    openSupplierLedger: suppliersUIEvents.openSupplierLedger,
    openKonnectDialog: suppliersUIEvents.openKonnectDialog,
    requiredLength: requiredLength,
    setRequiredLength: setRequiredLength,
    customCity,
    setCustomCity,
    refreshCount, 
    setRefreshCount
  };

  return <SuppliersUIContext.Provider value={value}>{children}</SuppliersUIContext.Provider>;
}
